import React from "react";
import Link from "next/link";
import { SectionTitle, sharedStyles, resolveImage } from "./_shared";
import { ASSET_URL } from "../../../config";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useEvents } from "../../../services/EventService";

const eventsStripStyles = `
.tr-events__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 420px));
  gap: clamp(16px, 2vw, 24px);
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto;
}
@media (max-width: 560px) {
  .tr-events__list { grid-template-columns: minmax(0, 420px); }
}

.tr-events__card {
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  background: white;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  color: inherit;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.tr-events__card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px rgba(0,0,0,.1);
  border-color: #d1d5db;
}

/* Image + overlays */
.tr-events__imgwrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
  overflow: hidden;
}
.tr-events__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-events__card:hover .tr-events__img { transform: scale(1.04); }
.tr-events__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--primary, #1b579e);
  background: color-mix(in srgb, var(--primary, #1b579e) 8%, white);
}

/* Badge date en coin (jour + mois) */
.tr-events__datebox {
  position: absolute;
  top: 12px;
  inset-inline-start: 12px;
  background: white;
  border-radius: 12px;
  padding: 8px 10px 6px;
  min-width: 54px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,.12);
  line-height: 1;
}
.tr-events__datebox__day {
  display: block;
  font-size: 22px;
  font-weight: 800;
  color: #1a1a2e;
}
.tr-events__datebox__month {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--primary, #1b579e);
  margin-top: 2px;
}

/* Pill "En cours" si évènement démarré */
.tr-events__live {
  position: absolute;
  top: 12px;
  inset-inline-end: 12px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(239,68,68,.4);
}
.tr-events__live::before {
  content: "";
  display: inline-block;
  width: 6px; height: 6px;
  background: white;
  border-radius: 50%;
  margin-inline-end: 6px;
  animation: tr-events-pulse 1.6s infinite;
  vertical-align: middle;
}
@keyframes tr-events-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .3; }
}

/* Body */
.tr-events__body {
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
}
.tr-events__name {
  font-size: 18px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.25;
  margin: 0;
  text-wrap: balance;
}
.tr-events__meta {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 13px;
  color: #4b5563;
}
.tr-events__meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.35;
}
.tr-events__meta-row svg {
  flex-shrink: 0;
  color: var(--primary, #1b579e);
}
.tr-events__desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Liens externes */
.tr-events__links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}
.tr-events__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
  color: var(--primary, #1b579e);
  text-decoration: none;
  transition: background .15s ease;
}
.tr-events__link:hover {
  background: color-mix(in srgb, var(--primary, #1b579e) 20%, white);
}

/* Footer de carte : CTA "Voir plus" */
.tr-events__more {
  margin-top: auto;
  padding-top: 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--primary, #1b579e);
}
.tr-events__card:hover .tr-events__more { text-decoration: underline; }

/* Empty state */
.tr-events__empty-row {
  display: flex; justify-content: center;
}
.tr-events__empty {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: color-mix(in srgb, var(--primary, #1b579e) 6%, white);
  border: 1px dashed color-mix(in srgb, var(--primary, #1b579e) 26%, white);
  border-radius: 999px;
  padding: 14px 22px;
  font-size: 14px;
  color: color-mix(in srgb, var(--primary, #1b579e) 40%, black);
}
`;

// Parse une date en ms. Null si invalide.
function toMs(v) {
    if (!v) return null;
    const ms = typeof v === "number" ? v : Date.parse(v);
    return isNaN(ms) ? null : ms;
}

// Début du jour courant (00:00 local) → tolérant pour afficher un évènement
// qui finit "aujourd'hui" même si sa dateFin est en début de matinée.
function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

// Formate un jour sur 2 chiffres, sans dépendance externe.
function formatDay(ms) {
    return new Date(ms).getDate();
}

// Mois abrégé (3 lettres, maj) dans la locale demandée.
function formatMonth(ms, lang) {
    return new Date(ms)
        .toLocaleDateString(lang || "fr", { month: "short" })
        .replace(".", "")
        .toUpperCase();
}

// Libellé de période :
// - Un seul jour : "samedi 12 avril · 20:30"
// - Plusieurs jours : "12 → 15 avril"
function formatPeriod(startMs, endMs, lang) {
    if (!startMs) return null;
    const start = new Date(startMs);
    const opts = { weekday: "long", day: "numeric", month: "long" };

    const timeOpts = { hour: "2-digit", minute: "2-digit" };
    const hasTime = start.getHours() !== 0 || start.getMinutes() !== 0;
    const timeStr = hasTime ? start.toLocaleTimeString(lang || "fr", timeOpts) : null;

    if (!endMs || Math.abs(endMs - startMs) < 24 * 3600 * 1000) {
        // même jour (ou pas de fin) → date + heure si dispo
        const dateStr = start.toLocaleDateString(lang || "fr", opts);
        return timeStr ? `${dateStr} · ${timeStr}` : dateStr;
    }

    // Multi-jours → "12 → 15 avril"
    const end = new Date(endMs);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
        const monthYear = end.toLocaleDateString(lang || "fr", { month: "long", year: "numeric" });
        return `${start.getDate()} → ${end.getDate()} ${monthYear}`;
    }
    const startStr = start.toLocaleDateString(lang || "fr", { day: "numeric", month: "short" });
    const endStr = end.toLocaleDateString(lang || "fr", { day: "numeric", month: "long", year: "numeric" });
    return `${startStr} → ${endStr}`;
}

// EventsStrip : grille riche des évènements en cours / à venir.
// Règle de visibilité : un évènement est affiché tant que sa date de fin
// n'est pas passée (fallback sur dateEvent si dateFin absent). Si un
// évènement est déjà commencé, un badge "En cours" apparaît.
// Source : /v1/events (fetch client). Si aucun évènement éligible → section masquée.
export default function EventsStrip({ context }) {
    const { org, lang } = context;
    // Événements fetchés CÔTÉ CLIENT via /v1/events (modèle galeries/actualités).
    const { events } = useEvents(context);

    const todayStart = startOfToday();
    const nowMs = Date.now();

    // Événements issus de /v1/events (context.events), plus de org.events.
    const upcoming = (events || [])
        .filter(e => e && e.active !== false && !e.hidden)
        .map(e => {
            const startMs = toMs(e.dateEvent || e.date_start || e.date);
            const endMs = toMs(e.dateFin || e.date_end) || startMs;
            return { e, startMs, endMs };
        })
        .filter(({ startMs, endMs }) => {
            if (!startMs) return false;
            const effectiveEnd = endMs || startMs;
            return effectiveEnd >= todayStart;
        })
        .sort((a, b) => (a.startMs || 0) - (b.startMs || 0))
        .slice(0, 3);

    if (upcoming.length === 0) return null;

    const cityName = org.ville || org.name;
    const resolvedTitle = t`Événements à ${cityName}`;
    const resolvedHighlight = t`Événements`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + eventsStripStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={resolvedTitle} highlight={resolvedHighlight} />

                <div className="tr-events__list">
                    {upcoming.map(({ e: event, startMs, endMs }) => {
                        const localized = applyLang(event, lang);
                        const imgSrc = event.image ? resolveImage(event.image, ASSET_URL) : null;
                        const href = org.BASE_WEB_URL + "/evenement/" + event.id;
                        const isMulti = !!event.isMultiSession;

                        const isLive = !isMulti && startMs <= nowMs && (endMs || startMs) >= nowMs;
                        const periodStr = isMulti ? null : formatPeriod(startMs, endMs, lang);
                        const address = localized.adresse || localized.address;
                        // Traduction lien par lien : chaque link porte ses propres
                        // fragments de langue (link.fr.name, ...) construits côté API.
                        const links = isMulti ? [] : (Array.isArray(event.links) ? event.links.filter(l => l?.url).map(l => applyLang(l, lang)) : []);

                        return (
                            <Link key={event.id} href={href} legacyBehavior>
                                <a className="tr-events__card">
                                    <span style={{display:"block"}} className="tr-events__imgwrap">
                                        {imgSrc ? (
                                            <img src={imgSrc} alt={localized.name || localized.title} className="tr-events__img" loading="lazy" />
                                        ) : (
                                            <span style={{display:"block"}} className="tr-events__img-placeholder">
                                                {renderIcon({ name: "IconCalendarEvent", source: "tabler" }, 48)}
                                            </span>
                                        )}

                                        {!isMulti && startMs && (
                                            <span style={{display:"block"}} className="tr-events__datebox">
                                                <span className="tr-events__datebox__day">{formatDay(startMs)}</span>
                                                <span className="tr-events__datebox__month">{formatMonth(startMs, lang)}</span>
                                            </span>
                                        )}

                                        {isLive && (
                                            <span className="tr-events__live">
                                                <Trans>En cours</Trans>
                                            </span>
                                        )}
                                    </span>

                                    <span style={{display:"block"}} className="tr-events__body">
                                        <h3 className="tr-events__name">{localized.name || localized.title}</h3>

                                        <span style={{display:"block"}} className="tr-events__meta">
                                            {isMulti && (
                                                <span className="tr-events__meta-row">
                                                    {renderIcon({ name: "IconCalendarEvent", source: "tabler" }, 14)}
                                                    <span><Trans>Plusieurs sessions disponibles</Trans></span>
                                                </span>
                                            )}
                                            {periodStr && (
                                                <span className="tr-events__meta-row">
                                                    {renderIcon({ name: "IconClock", source: "tabler" }, 14)}
                                                    <span>{periodStr}</span>
                                                </span>
                                            )}
                                            {address && (
                                                <span className="tr-events__meta-row">
                                                    {renderIcon({ name: "IconMapPin", source: "tabler" }, 14)}
                                                    <span>{address}</span>
                                                </span>
                                            )}
                                        </span>

                                        {localized.description && (
                                            <span style={{display:"block"}} className="tr-events__desc">{localized.description}</span>
                                        )}

                                        {links.length > 0 && (
                                            <span className="tr-events__links">
                                                {links.slice(0, 3).map((l, i) => (
                                                    <span
                                                        key={i}
                                                        role="link"
                                                        tabIndex={0}
                                                        className="tr-events__link"
                                                        onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); window.open(l.url, "_blank", "noopener,noreferrer"); }}
                                                    >
                                                        {renderIcon({ name: "IconExternalLink", source: "tabler" }, 12)}
                                                        {l.name || l.label || t`Lien`}
                                                    </span>
                                                ))}
                                            </span>
                                        )}

                                        <span className="tr-events__more">
                                            <Trans>Voir plus</Trans>
                                            {renderIcon({ name: "IconArrowRight", source: "tabler" }, 14)}
                                        </span>
                                    </span>
                                </a>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </>
    );
}
